import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthProvider';
import { normalizeKey, normalizeType } from '../utils/normalize';
import { cleanAndFillAbc } from '../utils/cleanAndFillAbc';

// Shared guided import modal for TheSession.org tunebook import.
// Used from both the onboarding flow and from Settings.
// Props:
//   onClose — called when user dismisses or finishes
//   afterImportPath — optional route to navigate to after success (e.g. '/search')

export default function TheSessionImportModal({ onClose, afterImportPath }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [phase, setPhase] = useState('input'); // 'input' | 'importing' | 'success' | 'error'
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [importedCount, setImportedCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const overlay = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    zIndex: 1200,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px', boxSizing: 'border-box',
  };

  const card = {
    backgroundColor: 'var(--bg-card, #fff)',
    color: 'var(--text-primary, #333)',
    borderRadius: '16px',
    maxWidth: '460px',
    width: '100%',
    padding: '28px 24px 22px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  };

  const btnPrimary = {
    padding: '10px 22px', borderRadius: '10px', border: 'none',
    backgroundColor: 'var(--text-link, #228be6)', color: '#fff',
    fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer',
    width: '100%', marginBottom: '8px',
  };

  const btnSecondary = {
    padding: '9px 18px', borderRadius: '10px',
    border: '1px solid var(--border-color, #ccc)',
    backgroundColor: 'transparent', color: 'var(--text-primary)',
    fontSize: '0.9rem', cursor: 'pointer',
  };

  const runImport = async () => {
    if (!username.trim()) return;
    setPhase('importing');
    setProgress({ done: 0, total: 0 });
    setErrorMsg('');

    // Extract numeric ID from a full URL if the user pasted one
    const rawInput = username.trim();
    const urlMatch = rawInput.match(/\/members\/(\d+)/);
    const userId = urlMatch ? urlMatch[1] : rawInput;

    try {
      const res = await fetch(`https://thesession.org/members/${encodeURIComponent(userId)}/tunebook?format=json`);
      if (!res.ok) throw new Error(`Could not find tunebook for ID "${userId}". Check that the member ID is correct.`);
      const data = await res.json();

      if (!Array.isArray(data.tunes) || data.tunes.length === 0) {
        throw new Error('No tunes found in that tunebook.');
      }

      const total = data.tunes.length;
      setProgress({ done: 0, total });
      let done = 0;

      for (const t of data.tunes) {
        try {
          const tuneRes = await fetch(`https://thesession.org/tunes/${t.id}?format=json`);
          const tuneData = await tuneRes.json();
          const setting = tuneData.settings?.[0] || {};

          const abcRaw = `${setting.abc || ''}`;
          const rawHeader = [
            `T:${tuneData.name || ''}`,
            `R:${tuneData.type || ''}`,
            `M:${setting.meter || ''}`,
            `L:${setting.length || ''}`,
            `Q:${setting.tempo || ''}`,
            `Z:${tuneData.transcribed_by || ''}`,
            `K:${setting.key || ''}`
          ].join('\n');

          const rawAbc = `${rawHeader}\n${abcRaw}`;
          const sanitizedAbc = cleanAndFillAbc({ rawAbc, type: tuneData.type, key: setting.key });

          const newTune = {
            title: tuneData.name || '',
            type: normalizeType(tuneData.type),
            key: normalizeKey(setting.key || ''),
            abc: sanitizedAbc,
            link1: `https://thesession.org/tunes/${t.id}`,
            genre: 'Irish',
            mode: 'tunes',
            status: '', chords: '', lyrics: '', notes: '',
            link2: '', link3: '', source: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            lastViewedAt: '', eventDate: '',
            sessions: [], recordings: [], customTags: [],
          };

          const tuneId = crypto.randomUUID();
          await setDoc(doc(db, 'users', user.uid, 'tunes', tuneId), { ...newTune, id: tuneId });
        } catch (_) {
          // Skip individual tune errors silently
        }
        done++;
        setProgress({ done, total });
      }

      setImportedCount(done);
      setPhase('success');
    } catch (err) {
      setErrorMsg(err.message || 'Import failed. Please try again.');
      setPhase('error');
    }
  };

  const handleSuccess = (goToLibrary) => {
    if (goToLibrary) {
      onClose?.();
      navigate('/search');
    } else {
      onClose?.();
    }
  };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={card}>

        {/* ── Input phase ──────────────────────────────────── */}
        {phase === 'input' && (
          <>
            <div style={{ fontSize: '1.8rem', marginBottom: '0.4rem', textAlign: 'center' }}>🎻</div>
            <h2 style={{ fontSize: '1.2em', margin: '0 0 0.5rem', fontWeight: 800, textAlign: 'center' }}>
              Import from TheSession.org
            </h2>
            <p style={{ margin: '0 0 0.5rem', lineHeight: 1.6, fontSize: '0.9rem', opacity: 0.9 }}>
              Enter a <a href="https://thesession.org" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-link, #228be6)' }}>thesession.org</a> member ID to import a public tunebook — title, type, key, and ABC notation for every bookmarked tune.
            </p>
            <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', opacity: 0.65, lineHeight: 1.5 }}>
              Find your ID in your profile URL: <em>thesession.org/members/<strong>12345</strong></em>. You can also paste the full URL.
            </p>

            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runImport()}
              placeholder="Member ID (e.g. 12345)"
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 14px', borderRadius: '10px',
                border: '1px solid var(--border-color, #ccc)',
                background: 'var(--bg-card)', color: 'var(--text-primary)',
                fontSize: '0.95rem', marginBottom: '14px',
              }}
            />

            <button style={btnPrimary} onClick={runImport} disabled={!username.trim()}>
              Import Tunebook →
            </button>
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={onClose}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.85rem', cursor: 'pointer', padding: '4px' }}
              >
                Not now — I'll do this later
              </button>
            </div>
          </>
        )}

        {/* ── Importing phase ───────────────────────────────── */}
        {phase === 'importing' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.6rem' }}>⏳</div>
            <h2 style={{ fontSize: '1.15em', margin: '0 0 0.6rem', fontWeight: 800 }}>
              Importing your tunebook…
            </h2>
            {progress.total > 0 ? (
              <>
                <p style={{ margin: '0 0 0.8rem', opacity: 0.85 }}>
                  {progress.done} of {progress.total} tunes imported
                </p>
                {/* Progress bar */}
                <div style={{
                  width: '100%', height: '8px', borderRadius: '4px',
                  background: 'var(--border-color, #e5e5e5)',
                  overflow: 'hidden', marginBottom: '1rem',
                }}>
                  <div style={{
                    height: '100%', borderRadius: '4px',
                    background: 'var(--text-link, #228be6)',
                    width: `${Math.round((progress.done / progress.total) * 100)}%`,
                    transition: 'width 0.3s',
                  }} />
                </div>
              </>
            ) : (
              <p style={{ margin: '0 0 1rem', opacity: 0.75 }}>Fetching tunebook…</p>
            )}
            <p style={{ margin: 0, fontSize: '0.82rem', opacity: 0.55 }}>
              This may take a moment for larger collections.
            </p>
          </div>
        )}

        {/* ── Success phase ────────────────────────────────── */}
        {phase === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.2rem', marginBottom: '0.5rem' }}>✅</div>
            <h2 style={{ fontSize: '1.2em', margin: '0 0 0.6rem', fontWeight: 800 }}>
              Import complete!
            </h2>
            <p style={{ margin: '0 0 1.4rem', lineHeight: 1.6, opacity: 0.9 }}>
              <strong>{importedCount} tune{importedCount !== 1 ? 's' : ''}</strong> imported from TheSession.org. They're in your library now.
            </p>
            <button style={btnPrimary} onClick={() => handleSuccess(true)}>
              Go to My Library →
            </button>
            <div style={{ textAlign: 'center' }}>
              <button style={btnSecondary} onClick={() => handleSuccess(false)}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* ── Error phase ──────────────────────────────────── */}
        {phase === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
            <h2 style={{ fontSize: '1.15em', margin: '0 0 0.6rem', fontWeight: 800 }}>
              Import failed
            </h2>
            <p style={{ margin: '0 0 1.4rem', lineHeight: 1.6, opacity: 0.85, fontSize: '0.9rem' }}>
              {errorMsg}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button style={{ ...btnSecondary, flex: 1 }} onClick={() => setPhase('input')}>
                Try Again
              </button>
              <button style={{ ...btnSecondary, flex: 1 }} onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
